import * as React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { CollectionForm } from '../components/CollectionForm';
import { Collection, CollectionFormData } from '../types';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { CollectionsService } from '@/services/collections-service';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info } from 'lucide-react';

export function EditCollectionPage() {
  const { id } = useParams({ from: '/_authenticated/collections/edit/$id' });
  const [collection, setCollection] = React.useState<Collection | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetchCollection();
  }, [id]);

  const fetchCollection = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetchedCollection = await CollectionsService.getCollection(id);
      setCollection(fetchedCollection);
    } catch (err) {
      console.error('Error fetching collection:', err);
      setError('Failed to load collection. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const navigate = useNavigate();

  const handleSubmit = async (data: CollectionFormData) => {
    setIsSaving(true);
    
    try {
      console.log('Updating collection:', data);
      await CollectionsService.updateCollection(id, data);
      // Navigate to the collections list after successful update
      navigate({ to: '/collections' });
    } catch (err) {
      console.error('Error updating collection:', err);
      toast.error('Failed to update collection. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate({ to: `/collections/${id}` });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading collection...</p>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <Alert variant="destructive">
        <Info className="h-4 w-4" />
        <AlertTitle>Collection Not Found</AlertTitle>
        <AlertDescription>
          The collection with ID {id} could not be found. It may have been deleted or you may not have permission to view it.
        </AlertDescription>
      </Alert>
    );
  }

  // Convert Collection to CollectionFormData
  const initialData: CollectionFormData = {
    name: collection.name,
    description: collection.description,
    status: collection.status,
    visibility: collection.visibility,
    documents: collection.documents?.map(doc => doc.id) || [],
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Edit Collection: ${collection.name}`}
        description="Update collection information and settings."
      />
      
      <Card>
        <CardContent className="pt-6">
          <CollectionForm 
            initialData={initialData} 
            onSubmit={handleSubmit} 
            onCancel={handleCancel}
            isLoading={isSaving} 
          />
        </CardContent>
      </Card>
    </div>
  );
}
